import { useEffect, useRef } from "react";
import type { RoomProduct } from "@b2bportal/lodging-api";
import { usePriceFormatterContext } from "@hopper-b2b/contexts";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ActionButton } from "@hopper-b2b/ui";
import {
  attachTouchDiagnostics,
  logElementStack,
} from "../../../utils/touchDiagnostics";
import { useTapHandlers } from "../../../utils/useTapHandlers";

export interface RoomDetailsCtaProps {
  onClick: () => void;
  selectedProduct: RoomProduct;
  message?: string;
}

export function RoomDetailsCta({
  onClick,
  selectedProduct,
  message,
}: RoomDetailsCtaProps) {
  const { t } = useI18nContext();
  const { formatPrice } = usePriceFormatterContext();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { onClick: onTapClick, ...tapPointerHandlers } = useTapHandlers({
    onTap: onClick,
    logName: "hotel_room_cta",
  });

  useEffect(() => {
    if (buttonRef.current) {
      logElementStack(buttonRef.current, "hotel_room_cta");

      const button = buttonRef.current.querySelector("button");
      if (button) {
        const cleanup = attachTouchDiagnostics(
          button as HTMLElement,
          "hotel_room_cta"
        );
        return cleanup;
      }
    }
  }, []);

  return (
    // Pointer handlers go on the wrapper (where pointerdown/up positions
    // are tracked); onClick stays on the actual button so a click that
    // doesn't bubble through pointer events still triggers the action.
    <div ref={buttonRef} {...tapPointerHandlers}>
      <ActionButton
        onClick={onTapClick}
        fullWidth
        size="medium"
        message={
          message ||
          t("reserveForPricePerNight", {
            price: formatPrice(
              selectedProduct?.nightlyDiscountAware.priceWithUnmanagedDiscounts
            ),
          })
        }
      />
    </div>
  );
}
